import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/components/AuthShell";
import api, { formatApiError } from "@/lib/api";

export function BackToLogin() {
  const { t } = useTranslation();
  return (
    <Link
      to="/login"
      className="mt-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      data-testid="back-to-login-link"
    >
      <ArrowLeft className="h-3.5 w-3.5" />
      {t("auth.back_to_login")}
    </Link>
  );
}

function SentNotice({ email }) {
  const { t } = useTranslation();
  return (
    <div data-testid="forgot-sent">
      <div className="h-12 w-12 rounded-2xl qrise-gradient flex items-center justify-center shadow-lg shadow-purple-500/20">
        <MailCheck className="h-5 w-5 text-white" />
      </div>
      <h1 className="mt-6 text-3xl font-semibold tracking-tight">{t("auth.forgot_sent_title")}</h1>
      <p className="mt-3 text-muted-foreground leading-relaxed" data-testid="forgot-sent-body">
        {t("auth.forgot_sent_body", { email })}
      </p>
      <BackToLogin />
    </div>
  );
}

export default function ForgotPassword() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", {
        email: email.trim(),
        language: i18n.language?.startsWith("en") ? "en" : "fr",
      });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell>
      {sent ? (
        <SentNotice email={email.trim()} />
      ) : (
        <form onSubmit={submit} data-testid="forgot-form">
          <h1 className="text-3xl font-semibold tracking-tight">{t("auth.forgot_title")}</h1>
          <p className="mt-2 text-muted-foreground">{t("auth.forgot_subtitle")}</p>
          <div className="mt-8">
            <Label htmlFor="forgot-email" className="text-xs font-medium">
              {t("auth.email")}
            </Label>
            <Input
              id="forgot-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              className="mt-1.5 h-11 rounded-xl"
              placeholder="you@company.com"
              data-testid="forgot-email-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="mt-6 w-full h-11 rounded-xl bg-foreground text-background hover:bg-foreground/90"
            data-testid="forgot-submit-btn"
          >
            {loading ? t("auth.forgot_sending") : t("auth.forgot_submit")}
          </Button>
          <BackToLogin />
        </form>
      )}
    </AuthShell>
  );
}
